import React, { useEffect, useState } from "react";
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Loader2 } from "lucide-react";
import api, { formatApiErrorDetail } from "@/lib/api";
import { toast } from "sonner";
import ClientPicker from "@/components/client/ClientPicker";

export const ADDITIONAL_TYPES = [
  "Audit", "Corporate Tax", "Corporate Tax Registration", "CT Deregistration", "Liquidation", "Undertaking Letter",
  "Special Purpose", "SRTIP", "Others",
];
export const ADDITIONAL_STATUSES = ["Pending", "In Progress", "Under Review", "Completed", "Filed"];
export const ADDITIONAL_INVOICE_STATUSES = ["Invoiced", "Pending", "Paid", "Partly Paid"];

const empty = {
  compliance_type: "Audit",
  client_id: "",
  client_name: "",
  report_number: "",
  partner_name: "",
  person_1: "",
  reviewer: "",
  fees: "",
  invoice_status: "Pending",
  jurisdiction: "",
  period: "",
  deadline: "",
  status: "Pending",
  remarks: "",
};

export default function AdditionalComplianceForm({ open, onClose, initial, onSaved, availableTypes }) {
  const [form, setForm] = useState(empty);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    if (open) {
      if (initial) {
        setForm({
          compliance_type: initial.compliance_type || "Audit",
          client_id: initial.client_id || "",
          client_name: initial.client_name || "",
          report_number: initial.report_number || "",
          partner_name: initial.partner_name || "",
          person_1: initial.person_1 || "",
          reviewer: initial.reviewer || "",
          fees: initial.fees ?? "",
          invoice_status: initial.invoice_status || "Pending",
          jurisdiction: initial.jurisdiction || "",
          period: initial.period || "",
          deadline: initial.deadline || "",
          status: initial.status || "Pending",
          remarks: initial.remarks || "",
        });
      } else {
        setForm({ ...empty, compliance_type: (availableTypes && availableTypes[0]) || empty.compliance_type });
      }
    }
  }, [open, initial]);

  const set = (patch) => setForm((f) => ({ ...f, ...patch }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.client_name) {
      toast.error("Client name is required");
      return;
    }
    setSaving(true);
    try {
      const payload = { ...form, fees: Number(form.fees) || 0 };
      if (initial) {
        await api.patch(`/additional-compliances/${initial.id}`, payload);
        toast.success("Additional Compliance updated");
      } else {
        await api.post("/additional-compliances", payload);
        toast.success("Additional Compliance created");
      }
      onSaved?.();
      onClose?.();
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed");
    } finally { setSaving(false); }
  };

  return (
    <Dialog open={open} onOpenChange={(o) => !o && onClose?.()}>
      <DialogContent className="max-w-2xl max-h-[92vh] overflow-y-auto">
        <DialogHeader>
          <DialogTitle className="font-display" data-testid="additional-form-title">
            {initial ? "Edit Additional Compliance" : "New Additional Compliance"}
          </DialogTitle>
          <DialogDescription>Audit, CT and other compliances (report tracking).</DialogDescription>
        </DialogHeader>
        <form onSubmit={submit} className="space-y-4" data-testid="additional-form">
          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-600">Type</Label>
              <Select value={form.compliance_type} onValueChange={(v) => set({ compliance_type: v })}>
                <SelectTrigger className="mt-1.5" data-testid="additional-type"><SelectValue /></SelectTrigger>
                <SelectContent>{(availableTypes || ADDITIONAL_TYPES).map((t) => <SelectItem key={t} value={t}>{t}</SelectItem>)}</SelectContent>
              </Select>
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-600">Client Name</Label>
              <div className="mt-1.5">
                <ClientPicker
                  value={form.client_id ? { id: form.client_id, name: form.client_name } : (form.client_name ? { id: "", name: form.client_name } : null)}
                  onChange={(c) => set({ client_id: c?.id || "", client_name: c?.name || "" })}
                  testid="additional-client-picker"
                />
              </div>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-600">Report Number</Label>
              <Input value={form.report_number} onChange={(e) => set({ report_number: e.target.value })}
                     className="mt-1.5 font-mono-num" data-testid="additional-report-number" placeholder="e.g. AUD-2026-001" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-600">Partner Name</Label>
              <Input value={form.partner_name} onChange={(e) => set({ partner_name: e.target.value })}
                     className="mt-1.5" data-testid="additional-partner-name" />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-600">Person 1</Label>
              <Input value={form.person_1} onChange={(e) => set({ person_1: e.target.value })}
                     className="mt-1.5" data-testid="additional-person-1" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-600">Reviewer</Label>
              <Input value={form.reviewer} onChange={(e) => set({ reviewer: e.target.value })}
                     className="mt-1.5" data-testid="additional-reviewer" />
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-600">Fees (AED)</Label>
              <Input type="number" min="0" step="1" value={form.fees}
                     onChange={(e) => set({ fees: e.target.value })}
                     placeholder="0"
                     className="mt-1.5 font-mono-num" data-testid="additional-fees" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-600">Invoice Status</Label>
              <Select value={form.invoice_status} onValueChange={(v) => set({ invoice_status: v })}>
                <SelectTrigger className="mt-1.5" data-testid="additional-invoice-status"><SelectValue /></SelectTrigger>
                <SelectContent>{ADDITIONAL_INVOICE_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </div>
          </div>

          <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-600">Period</Label>
              <Input value={form.period} onChange={(e) => set({ period: e.target.value })}
                     className="mt-1.5" data-testid="additional-period" placeholder="e.g. FY 2025 / Q3-2026" />
            </div>
            <div>
              <Label className="text-xs uppercase tracking-widest text-slate-600">Deadline</Label>
              <Input
                type="date"
                value={form.deadline || ""}
                onChange={(e) => set({ deadline: e.target.value })}
                className="mt-1.5 font-mono-num"
                data-testid="additional-deadline"
              />
            </div>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-widest text-slate-600">Jurisdiction <span className="text-slate-400 normal-case tracking-normal font-normal">(auto from client)</span></Label>
            <Input value={form.jurisdiction} onChange={(e) => set({ jurisdiction: e.target.value })}
                   className="mt-1.5" data-testid="additional-jurisdiction" placeholder="Auto-filled from Client Master" />
          </div>

          <div>
            <Label className="text-xs uppercase tracking-widest text-slate-600">Status</Label>
            <Select value={form.status} onValueChange={(v) => set({ status: v })}>
              <SelectTrigger className="mt-1.5" data-testid="additional-status"><SelectValue /></SelectTrigger>
              <SelectContent>{ADDITIONAL_STATUSES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
            </Select>
          </div>

          <div>
            <Label className="text-xs uppercase tracking-widest text-slate-600">Remarks</Label>
            <Textarea
              value={form.remarks}
              onChange={(e) => set({ remarks: e.target.value })}
              rows={2}
              className="mt-1.5"
              data-testid="additional-remarks"
              placeholder="Free-form notes (e.g. blockers, pending client info)"
            />
          </div>

          <DialogFooter>
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={saving} data-testid="additional-save">
              {saving && <Loader2 className="w-4 h-4 mr-2 animate-spin" />}
              {initial ? "Save changes" : "Create record"}
            </Button>
          </DialogFooter>
        </form>
      </DialogContent>
    </Dialog>
  );
}
